import { useContext, useState, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { message } from 'antd'

import { commonUtils, eventManager } from 'utils'
import Forms from 'contexts/Forms'
import { commonHelper } from 'helpers'
import { calculateInterestProjection, convertToAnnualInterestDataSource } from 'utils/interestCalculations'
import { InterestModellingFormData } from 'components/InterestModellingForm'

import {
  policySummaryColumns,
  generatePolicySummaryDataSource,
  generateFreeSummaryDataSource,
  freeSummaryColumns,
  annualInterestColumns,
  generateGroups,
} from './helper'
import { addQuoteDetailsMutation } from 'gql/client'
import { useCustomMutation } from 'services/shared/mutation'
import { AddQuoteDetailsPayload } from 'services/shared/mutation.payload'
import { IQuickSearch } from 'interfaces/quickSearch.interface'
import { ISuccessfulQuote } from 'interfaces/quoteResult.interface'
import { AuthContext } from 'contexts'
import config from 'config'

const useQuoteDetails = () => {
  const [loading, setLoading] = useState(false)
  const addQuoteDetails = useCustomMutation<AddQuoteDetailsPayload>({ mutation: addQuoteDetailsMutation })
  const { setQuoteResultActiveTab } = useContext(Forms)
  const { user: currentUser } = useContext(AuthContext)
  const navigate = useNavigate()
  const urlParams = new URLSearchParams(window.location.search)
  const id = urlParams.get('id')
  const idNumber = Number(urlParams.get('idNumber'))
  const quoteData: ISuccessfulQuote = JSON.parse(localStorage.getItem('quoteResult') || '{}').successFullQuotes.find(
    (item: any) => item.$['perqresp:quote_response_identifier'] === id,
  )

  const data = quoteData.provider_success?.[0].quote_results?.[0]
  const quickSearchData: IQuickSearch = JSON.parse(localStorage.getItem('quickSearch') || '{}')
  const clientDetails: any = quickSearchData.clientDetails.firstLife

  // Initialize interest modelling form with quote data
  const [interestFormData, setInterestFormData] = useState<InterestModellingFormData>({
    youngerAge: 65,
    projectionBasis: 'age',
    customTerm: 20,
    initialLoan: Number(data?.payments?.[0]?.initial_only?.[0]?.initial_amount?.[0]?._ || 100000),
    feesAdded: Number(data?.fees?.[0]?.total?.[0]?._ || 5000),
    interestBasis: data?.interest_rates?.[0]?.to_be_applied?.[0] === 'Monthly' ? 'MER' : 'AER',
    interestRate:
      Number(data?.interest_rates?.[0]?.to_be_applied?.[0] === 'Monthly' ? data?.interest_rates?.[0]?.mer?.[0] : data?.interest_rates?.[0]?.aer?.[0]) || 5.0,
    propertyValue: Number(quickSearchData?.propertyAndLanding?.propertyValue || 250000),
    propertyGrowth: 3.5,
    includeRepayments: false,
    repaymentType: 'monthly',
    monthlyAmount: 0,
    interestPercent: 0,
    repaymentTerm: 0,
    applyDrawdown: false,
    drawdownAmount: 0,
    drawdownYear: 0,
  })

  // Calculate projections based on form data
  const { results: calculationResults, chartData } = useMemo(() => {
    return calculateInterestProjection(interestFormData)
  }, [interestFormData])

  // Convert to existing data source format
  const annualInterestDataSource = useMemo(() => {
    return convertToAnnualInterestDataSource(calculationResults)
  }, [calculationResults])

  const handleInterestFormChange = useCallback((field: keyof InterestModellingFormData, value: any) => {
    setInterestFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }, [])

  const policySummaryDataSource = generatePolicySummaryDataSource(data)
  const freeSummaryDataSource = generateFreeSummaryDataSource(data)
  const groups = generateGroups(interestFormData.propertyGrowth)

  const handleLinkRedirect = () => {
    try {
      // Get transaction number and quote response identifier from current quote
      const transactionNumber = Number(quoteData.$['perqresp:service_provider_reference'])
      const quoteResponseIdentifier = quoteData.$['perqresp:quote_response_identifier']

      // Use current user credentials
      const username = currentUser?.iressLoginId
      const password = currentUser?.iressPassword
      const userId = currentUser?.iressUserId

      // Generate dynamic timestamps
      const now = new Date()
      const expires = new Date(now.getTime() + 11 * 60 * 1000) // 11 minutes from now

      // Prepare auth data with user credentials and dynamic timestamps
      const authData = `<epreq:exweb_portal xmlns:epreq="http://www.exchange.co.uk/schema/ExwebPortalRequest/v1">
            <epreq:token>
              <wsse:Security xmlns:wsse="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-secext-1.0.xsd" xmlns:wsu="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-utility-1.0.xsd">
                <wsu:Timestamp>
                  <wsu:Created>${now.toISOString()}</wsu:Created>
                  <wsu:Expires>${expires.toISOString()}</wsu:Expires>
                </wsu:Timestamp>
                <wsse:UsernameToken>
                  <wsse:Username>${username}</wsse:Username>
                  <wsse:Password>${password}</wsse:Password>
                  <wsu:Created>${now.toISOString()}</wsu:Created>
                </wsse:UsernameToken>
              </wsse:Security>
            </epreq:token>
            <epreq:service_id>PerformQuoteSearchExtranetV1</epreq:service_id>
          </epreq:exweb_portal>`

      // Generate dynamic IDs and timestamps for XML data
      const messageId = crypto.randomUUID()
      const orchestrationId = crypto.randomUUID()
      const messageTimestamp = now.toISOString()

      // Prepare XML data with dynamic values and user ID
      const xmlData = `<?xml version="1.0" encoding="utf-8"?>
            <esreq:exchange_services xmlns:esreq="http://www.exchange.co.uk/schema/ExchangeServicesRequest/v1">
              <esreq:header>
                <esreq:message_timestamp>${messageTimestamp}</esreq:message_timestamp>
                <esreq:message_id>${messageId}</esreq:message_id>
                <esreq:message_schema>/exchange/exchangeservices/1.0/ExchangeServicesRequestV1.xsd</esreq:message_schema>
                <esreq:service_id>PerformQuoteSearchExtranetV1</esreq:service_id>
                <esreq:integrator_id>${config.IRESS_INTEGRATOR_ID}</esreq:integrator_id>
                <esreq:orchestration_id>${orchestrationId}</esreq:orchestration_id>
                <esreq:business_contract_id>${config.IRESS_BUSINESS_CONTRACT_ID}</esreq:business_contract_id>
                <esreq:user_id>${userId}</esreq:user_id>
                <esreq:payload_provider>Exchange Services</esreq:payload_provider>
                <esreq:payload_schema>/exchange/exchangeservices/1.2/PerformQuoteSearchExtranetRequestV1.xsd</esreq:payload_schema>
                <esreq:service_provider_id>Exchange Services</esreq:service_provider_id>
              </esreq:header>
              <esreq:payload>
                <pqsereq:perform_quotesearch_extranet_request
                  xmlns:pqsereq="http://www.exchange.co.uk/schema/PerformQuoteSearchExtranetRequest/v1">
                  <pqsereq:transaction_number
                    pqsereq:auto_launch_transaction="true"
                    pqsereq:quote_response_identifier="${quoteResponseIdentifier}"
                    pqsereq:auto_perform_kfi="true">
                    ${transactionNumber}
                  </pqsereq:transaction_number>
                </pqsereq:perform_quotesearch_extranet_request>
              </esreq:payload>
            </esreq:exchange_services>`

      // Use endpoint from config
      let endpoint
      if (process.env.REACT_APP_STAGE === 'production') {
        endpoint = `https://exweb.exchange.uk.com/Private/Elf/Public/elfExwebResponder.aspx`
      } else {
        endpoint = `https://ifapreview.exchange.uk.com/Private/Elf/Public/elfExwebResponder.aspx`
      }

      // Create and submit form directly to open in new tab
      const form = document.createElement('form')
      form.target = '_blank'
      form.action = endpoint
      form.method = 'post'
      form.style.display = 'none'

      // Create hidden inputs
      const authInput = document.createElement('input')
      authInput.type = 'hidden'
      authInput.name = 'auth'
      authInput.value = authData

      const browserInput = document.createElement('input')
      browserInput.type = 'hidden'
      browserInput.name = 'browser'
      browserInput.value = ''

      const xmldataInput = document.createElement('input')
      xmldataInput.type = 'hidden'
      xmldataInput.name = 'xmldata'
      xmldataInput.value = xmlData

      // Append inputs to form
      form.appendChild(authInput)
      form.appendChild(browserInput)
      form.appendChild(xmldataInput)

      // Submit form
      document.body.appendChild(form)
      form.submit()
      document.body.removeChild(form)

      message.success('Opening KFI in new tab...')
    } catch (error) {
      console.error('Error submitting exchange request:', error)
      message.error('Failed to open KFI')
    }
  }

  const currentDate = new Date()
  const formattedDate = currentDate.toLocaleDateString('en-GB')
  const {
    propertyAndLanding: { propertyValue },
  }: IQuickSearch = JSON.parse(localStorage.getItem('quickSearch') || '{}')

  const avatar = commonUtils.getAvatarFromProviderName(quoteData.provider_success[0].provider_product_data[0].provider_name[0])

  const handleQuoteInputRedirect = () => {
    navigate('/quote-result')
    setQuoteResultActiveTab('QUOTE INPUT')
  }

  const handleGoBackBtnClick = (clientIdNumber?: number) => {
    navigate(`/quote-result?idNumber=${clientIdNumber ? clientIdNumber : idNumber}`)
  }

  const handleConvert = async () => {
    const transactionNumber = Number(quoteData.$['perqresp:service_provider_reference'])
    let clientIdNumber

    if (idNumber > 0) {
      clientIdNumber = idNumber
    } else {
      clientIdNumber = commonUtils.generateIdNumber()
    }

    const dataToSave = {
      idNumber: Number(clientIdNumber),
      transactionNumber,
      userInput: {
        ...quickSearchData,
        propertyAndLanding: {
          ...quickSearchData.propertyAndLanding,
          propertyValue: Number(quickSearchData.propertyAndLanding.propertyValue),
          paymentAmountOnInitialAdvance: Number(quickSearchData.propertyAndLanding.paymentAmountOnInitialAdvance),
          leasePeriod: Number(quickSearchData.propertyAndLanding.leasePeriod),
          houseNameOrNumber: String(quickSearchData.propertyAndLanding.houseNameOrNumber),
        },
        furtherInformation: {
          ...quickSearchData.furtherInformation,
          adviceFee: Number(quickSearchData.furtherInformation.adviceFee),
        },
      },
      quoteDetails: {
        isCashbackConsidered: data.cashback_considered_ind?.[0] === 'true' ? true : false,
        isCashbackOffered: data.cashback_ind[0] === 'true' ? true : false,
        hasDownsizingProtection: data.downsizing_protection_ind[0] === 'true' ? true : false,
        hasFreeRepaymentOnFirstDeath: data.free_repayment_on_first_death_ind[0] === 'true' ? true : false,
        hasFreeValuations: data.free_valuations_ind[0] === 'true' ? true : false,
        hasGuaranteedDrawdown: data.guaranteed_drawdown_ind[0] === 'true' ? true : false,
        hasMedicalUnderwriting: data.medical_underwriting_ind[0] === 'true' ? true : false,
        hasOptionalRepayments: data.optional_repayments_ind[0] === 'true' ? true : false,
        optionalRepaymentType: data.optional_repayment_type[0],
        commissionPaidByProduct: Number(data.commission_paid[0]._),
        totalCostOfLoan: Number(data.total_cost_of_loan?.[0]._),
        netLoanAmount: Number(data.net_loan_amount[0]._),
        costPerPound: Number(data.cost_per_pound?.[0]._),
        notes: data.notes?.[0] || '',
        interestRates: {
          aer: Number(data.interest_rates[0].aer[0]),
          apr: Number(data.interest_rates[0].apr[0]),
          mer: Number(data.interest_rates[0].mer[0]),
          toBeApplied: data.interest_rates[0].to_be_applied[0],
          type: data.interest_rates[0].type[0],
        },
        documents: {
          application: data.documents?.[0].application?.[0],
          illustration: data.documents?.[0].illustration?.[0],
          key_features_document: data.documents?.[0].key_features_document?.[0],
          ltv_table: data.documents?.[0].ltv_table?.[0].document_details?.[0],
          other: data.documents?.[0].other?.[0].document_details?.[0],
        },
        earlyRepaymentCharge: {
          description: data.early_repayment_charge?.[0].description?.[0],
          type: data.early_repayment_charge?.[0].type?.[0],
          years: Number(data.early_repayment_charge?.[0].years?.[0] || 0),
        },
        initialAmount: Number(data.payments?.[0]?.initial_only?.[0]?.initial_amount?.[0]?._),
        fees: {
          applicationFee: Number(data.fees[0].application_fee[0]._),
          canApplicationFeeBeAddedToLoan: data.fees[0].application_fee_can_be_added_to_loan[0] === 'true' ? true : false,
          applicationFeeType: data.fees[0].application_fee_type[0],
          completionFee: Number(data.fees[0].completion_fee[0]._),
          canCompletionFeeBeAddedToLoan: data.fees[0].completion_fee_can_be_added_to_loan[0] === 'true' ? true : false,
          completionFeeType: data.fees[0].completion_fee_type[0],
          feeContributionAmount: Number(data.fees?.[0].fee_contribution_details?.[0].fee_contribution_amount?.[0]._),
          total: Number(data.fees?.[0].total?.[0]._),
          valuationFee: Number(data.fees?.[0].valuation_fee?.[0]._),
          canValuationFeeBeAddedToLoan: data.fees?.[0].valuation_fee_can_be_added_to_loan?.[0] === 'true' ? true : false,
          valuationFeeType: data.fees?.[0].valuation_fee_type?.[0],
        },
      },
    }

    const { errors } = await addQuoteDetails(dataToSave)
    if (errors?.length) {
      setLoading(false)
      const errMessage = commonHelper.parseGraphqlErrorMessage(errors)
      message.error(errMessage)
      return
    }
    message.success('Operation finished successfully')
    handleGoBackBtnClick(clientIdNumber)
  }

  const handleConvertModal = () => {
    eventManager.openConfirmModal(
      { header: 'Do you want to convert this lead as “Fact Find“' },
      {
        confirm: () => handleConvert(),
      },
    )
  }

  return {
    policySummaryColumns,
    policySummaryDataSource,
    freeSummaryColumns,
    freeSummaryDataSource,
    annualInterestColumns,
    annualInterestDataSource,
    groups,
    quoteData,
    clientDetails,
    handleLinkRedirect,
    formattedDate,
    propertyValue,
    avatar,
    handleQuoteInputRedirect,
    interestFormData,
    handleInterestFormChange,
    calculationResults,
    chartData,
    handleGoBackBtnClick,
    loading,
    handleConvertModal,
  }
}

export default useQuoteDetails
